import { useEffect, useRef, useState } from "react";
import { Sidebar } from "@/react/components/Events/NewEventsSpace/EventDetailPage/Sidebar";
import {
  canRespondToInvitation,
  isEventFinished,
} from "@/react/components/Events/NewEventsSpace/shared/helpers";
import { STATUS } from "@/react/components/Events/helpers/constants";
import { Carousel } from "@/react/components/Spaces/ImageSpace/FeedView/Carousel";
import { useCurrentPostContext } from "@circle-react/contexts";
import { isEventPost, isImagePost } from "@circle-react/helpers/postHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { usePostList } from "@circle-react/hooks/posts/usePostList";
import type { Attachment } from "@circle-react/types/Attachment";
import { Modal } from "@circle-react-shared/uikit/ModalV2";
import { TranscriptsPlayer } from "@circle-react-uikit/TranscriptsPlayer";
import { CommentsSection } from "../PostViewListItem/CommentsSection";
import { EngagementActions } from "../PostViewListItem/EngagementActions";
import { PostBody } from "../PostViewListItem/PostBody";
import { PostCoverImage } from "../PostViewListItem/PostCoverImage";
import { PostHeader } from "../PostViewListItem/PostHeader";
import { PostSummary } from "../PostViewListItem/PostSummary";
import { useCurrentPostHelpers } from "../useCurrentPostHelpers";
import { PostBodySkeleton } from "./SkeletonLoader/PostBodySkeleton";
import { useCommentFocus } from "./hooks/useCommentFocus";

export interface ModalBodyProps {
  autofocusReplyForm?: boolean;
  isLoading?: boolean;
  isRenderedOutsideTheSpace?: boolean;
  onPostDestroy: () => void;
  onScroll?: (scrollTop: number) => void;
  shouldOpenCommentPage?: boolean;
}

export const ModalBody = ({
  autofocusReplyForm = true,
  isRenderedOutsideTheSpace = false,
  onPostDestroy,
  shouldOpenCommentPage = false,
  isLoading = false,
  onScroll,
}: ModalBodyProps) => {
  const { record: post, onPostChange } = useCurrentPostContext();

  const {
    status,
    is_comments_enabled: isCommentsEnabled,
    event_setting_attributes: event_setting,
  } = post;

  const { isPostBodyEmpty } = useCurrentPostHelpers();

  const { refetch: refetchPosts } = usePostList();

  const doesPostHaveCoverImage = Boolean(post?.cover_image_url);

  const liveRoomSettingAttributes =
    event_setting?.live_stream_room_setting_attributes;
  const recordingAttachment =
    liveRoomSettingAttributes?.recording_attributes as
      | Attachment
      | null
      | undefined;
  const postingRecording =
    liveRoomSettingAttributes?.auto_post_recording_enabled;

  const [isContentVisible, setIsContentVisible] = useState(!isLoading);

  const isPublished = status === STATUS.PUBLISHED;
  const shouldDisplayRsvpAction =
    isEventPost(post) &&
    isPublished &&
    !isEventFinished(post) &&
    canRespondToInvitation(post);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (!isLoading) {
      timer = setTimeout(() => setIsContentVisible(true), 50);
    } else {
      setIsContentVisible(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isLoading]);

  const {
    commentsFormRef,
    showCommentsAndFocusOnForm,
    toggleComment,
    shouldShowComments,
  } = useCommentFocus({
    initialFocus: autofocusReplyForm,
  });

  const modalRef = useRef<HTMLDivElement>(null);

  if (isLoading) {
    return <PostBodySkeleton />;
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = event.currentTarget;
    onScroll?.(scrollTop);
  };

  return (
    <Modal.Body className="!p-0" ref={modalRef} onScroll={handleScroll}>
      <PostSummary post={post} shouldScrollIntoView />
      <div
        className={classNames(
          "flex flex-col gap-5",
          "transition-opacity duration-300 ease-in-out",
          {
            "opacity-0": !isContentVisible,
            "opacity-100": isContentVisible,
          },
        )}
      >
        <PostCoverImage post={post} className="md:!rounded-none" />
        <div
          className={classNames("px-5 md:px-8", {
            "pt-3": !doesPostHaveCoverImage,
          })}
        >
          <PostHeader
            post={post}
            onChange={onPostChange}
            refetchPosts={refetchPosts}
            hideShareLink
            hideBookmarkButton
            hidePinnedToTopIcon
            onPostDestroy={onPostDestroy}
            hideAuthorHeadline={false}
            tagsLimit={1}
            tagsShowMoreLabelVariant="numbers"
            metaInfoWrapperClassName=""
            isRenderedOutsideTheSpace={isRenderedOutsideTheSpace}
          />
          <div
            className={classNames("space-y-5", {
              "mt-5": isPostBodyEmpty,
            })}
          >
            {isEventPost(post) && (
              <Sidebar
                onChange={post => {
                  onPostChange?.(post);
                }}
                shouldDisplayRsvpAction={shouldDisplayRsvpAction}
              />
            )}
            {recordingAttachment && postingRecording && (
              <TranscriptsPlayer
                src={recordingAttachment.url}
                type={recordingAttachment.content_type}
                mediaTranscoding={recordingAttachment.media_transcoding}
                mediaTranscriptId={recordingAttachment.media_transcript_id}
                isDownloadable={recordingAttachment.is_downloadable}
                poster={recordingAttachment.image_variants?.thumbnail}
                downloadUrl={recordingAttachment.url}
                chaptersUrl={recordingAttachment.chapters_url}
                previewThumbnailsUrl={
                  recordingAttachment.preview_thumbnails_url
                }
                variants={recordingAttachment.video_variants}
                signedId={recordingAttachment.signed_id}
              />
            )}
            {isImagePost(post) && (
              <Carousel
                onPostChange={onPostChange}
                onPostDestroy={onPostDestroy}
              />
            )}
            <PostBody />
            <div>
              <EngagementActions
                className="!px-0"
                toggleComment={toggleComment}
                showCommentAndFocusCommentForm={showCommentsAndFocusOnForm}
                shouldOpenCommentPage={shouldOpenCommentPage}
              />
              {isCommentsEnabled && shouldShowComments && (
                <CommentsSection
                  commentsFormRef={commentsFormRef}
                  autofocusReplyForm={false}
                  scrollableTarget={modalRef}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal.Body>
  );
};
